import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  fetchCollectionSales,
  fetchMotiveSales,
  fetchTopicGroupSales,
  fetchTopicSales,
} from '@/api/sales/api'
import type {
  CollectionSale,
  MotiveSale,
  SalesPage,
  SalesQuery,
  TopicGroupSale,
  TopicSale,
} from '@/api/sales/types'

/*
 * Ein Hook je Ebene, alle vier werden von der Seite unbedingt aufgerufen und
 * über `enabled` an- und ausgeschaltet. React verbietet bedingte Hooks, ein
 * einzelner generischer Hook mit einem Level-Parameter würde entweder gegen
 * diese Regel verstoßen oder die vier unterschiedlichen Rückgabetypen auf
 * einen unsicheren gemeinsamen Nenner zwingen.
 */

const COLLECTION_KEY = 'sales-collection' as const
const MOTIVE_KEY = 'sales-motive' as const
const TOPIC_GROUP_KEY = 'sales-topic-group' as const
const TOPIC_KEY = 'sales-topic' as const

export function useCollectionSales(
  query: SalesQuery,
  enabled: boolean,
): UseQueryResult<SalesPage<CollectionSale>> {
  return useQuery({
    queryKey: [COLLECTION_KEY, query],
    queryFn: ({ signal }) => fetchCollectionSales(query, signal),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })
}

export function useMotiveSales(
  query: SalesQuery,
  enabled: boolean,
): UseQueryResult<SalesPage<MotiveSale>> {
  return useQuery({
    queryKey: [MOTIVE_KEY, query],
    queryFn: ({ signal }) => fetchMotiveSales(query, signal),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })
}

export function useTopicGroupSales(
  query: SalesQuery,
  enabled: boolean,
): UseQueryResult<SalesPage<TopicGroupSale>> {
  return useQuery({
    queryKey: [TOPIC_GROUP_KEY, query],
    queryFn: ({ signal }) => fetchTopicGroupSales(query, signal),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })
}

export function useTopicSales(
  query: SalesQuery,
  enabled: boolean,
): UseQueryResult<SalesPage<TopicSale>> {
  return useQuery({
    queryKey: [TOPIC_KEY, query],
    queryFn: ({ signal }) => fetchTopicSales(query, signal),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })
}
